import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, XCircle, Loader2, RotateCcw, Square } from 'lucide-react';
import { buildsApi, createBuildWs } from '../api/client';
import { formatDistanceToNow } from 'date-fns';

const STEP_STATUS = {
  success:        { icon: CheckCircle2, cls: 'text-green-400',              bg: 'bg-green-500/5 border-green-500/20' },
  SUCCESS:        { icon: CheckCircle2, cls: 'text-green-400',              bg: 'bg-green-500/5 border-green-500/20' },
  running:        { icon: Loader2,      cls: 'text-purple-400 animate-spin', bg: 'bg-purple-500/5 border-purple-500/20' },
  WORKING:        { icon: Loader2,      cls: 'text-purple-400 animate-spin', bg: 'bg-purple-500/5 border-purple-500/20' },
  failed:         { icon: XCircle,      cls: 'text-red-400',                bg: 'bg-red-500/5 border-red-500/20' },
  FAILURE:        { icon: XCircle,      cls: 'text-red-400',                bg: 'bg-red-500/5 border-red-500/20' },
  queued:         { icon: Loader2,      cls: 'text-slate-500',              bg: 'bg-slate-50 border-slate-200' },
  QUEUED:         { icon: Loader2,      cls: 'text-slate-500',              bg: 'bg-slate-50 border-slate-200' },
};

function classifyLine(text) {
  if (/^(DONE|PUSH|BUILD|RUN|ERROR|FETCH)\b/.test(text)) return 'step';
  if (/successfully|success|finished/i.test(text)) return 'success';
  if (/error|failed|fatal/i.test(text)) return 'error';
  if (/^starting build|^pulling source/i.test(text)) return 'info';
  return 'log';
}

const LOG_COLORS = {
  step:    'text-cyan-400 font-semibold',
  success: 'text-green-400',
  error:   'text-red-400',
  info:    'text-blue-400',
  log:     'text-slate-700',
};

export default function BuildDetail() {
  const { buildId } = useParams();
  const [build, setBuild] = useState(null);
  const [logs, setLogs] = useState([]);
  const [wsStatus, setWsStatus] = useState('connecting'); // connecting | live | done | error
  const logsEndRef = useRef(null);
  const wsRef = useRef(null);

  // Auto-scroll on new log lines
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  // Load initial build data
  useEffect(() => {
    buildsApi.get(buildId)
      .then(setBuild)
      .catch(() => setBuild({ id: buildId, status: 'unknown', steps: [] }));
  }, [buildId]);

  // Open WebSocket for live logs
  useEffect(() => {
    const ws = createBuildWs(buildId);
    wsRef.current = ws;

    ws.onopen = () => setWsStatus('live');

    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);

      if (msg.type === 'log') {
        const level = classifyLine(msg.line);
        setLogs(l => [...l, { text: msg.line, level }]);
      }

      if (msg.type === 'status') {
        setBuild(b => b ? {
          ...b,
          status: msg.buildStatus,
          startTime: msg.startTime?.seconds
            ? new Date(Number(msg.startTime.seconds) * 1000).toISOString()
            : b.startTime,
        } : b);
      }

      if (msg.type === 'step') {
        setBuild(b => {
          if (!b) return b;
          const steps = [...(b.steps || [])];
          if (steps[msg.stepIndex]) {
            steps[msg.stepIndex] = { ...steps[msg.stepIndex], status: msg.status };
          }
          return { ...b, steps };
        });
      }

      if (msg.type === 'done') {
        setWsStatus('done');
        setBuild(b => b ? { ...b, status: msg.status } : b);
      }

      if (msg.type === 'error') {
        setLogs(l => [...l, { text: `[error] ${msg.message}`, level: 'error' }]);
      }
    };

    ws.onclose = () => setWsStatus(s => s === 'live' ? 'done' : s);
    ws.onerror = () => setWsStatus('error');

    return () => ws.close();
  }, [buildId]);

  const handleCancel = async () => {
    try {
      await buildsApi.cancel(buildId);
    } catch (e) {
      console.error('Cancel failed', e);
    }
  };

  const handleRetry = async () => {
    try {
      await buildsApi.retry(buildId);
    } catch (e) {
      console.error('Retry failed', e);
    }
  };

  const isRunning = build?.status === 'running' || build?.status === 'WORKING' || build?.status === 'QUEUED';

  return (
    <div className="p-6 space-y-5 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/builds" className="btn-ghost py-1.5 px-2.5 text-xs">
          <ArrowLeft size={14} /> Builds
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-slate-900 font-mono">{buildId}</h2>
            {isRunning && (
              <span className="flex items-center gap-1.5 text-xs text-purple-400">
                <Loader2 size={12} className="animate-spin" /> Running
              </span>
            )}
            {(build?.status === 'SUCCESS' || build?.status === 'success') && (
              <span className="flex items-center gap-1.5 text-xs text-green-400">
                <CheckCircle2 size={12} /> Success
              </span>
            )}
            {(build?.status === 'FAILURE' || build?.status === 'failed') && (
              <span className="flex items-center gap-1.5 text-xs text-red-400">
                <XCircle size={12} /> Failed
              </span>
            )}
          </div>
          {build && (
            <p className="text-xs text-slate-500 mt-0.5">
              {build.repo} · {build.branch}
              {build.commit && <> · <span className="font-mono">{build.commit}</span></>}
              {build.startTime && <> · Started {formatDistanceToNow(new Date(build.startTime), { addSuffix: true })}</>}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isRunning && (
            <button onClick={handleCancel} className="btn-danger py-1.5 px-3 text-xs">
              <Square size={12} /> Cancel
            </button>
          )}
          {['success', 'failed', 'cancelled', 'SUCCESS', 'FAILURE', 'CANCELLED'].includes(build?.status) && (
            <button onClick={handleRetry} className="btn-ghost py-1.5 px-3 text-xs">
              <RotateCcw size={13} /> Retry
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Steps */}
        <div className="space-y-2">
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Build Steps</p>
          {!build && (
            <div className="card p-3 text-xs text-slate-500">Loading…</div>
          )}
          {(build?.steps || []).map((step, i) => {
            const sc = STEP_STATUS[step.status] || STEP_STATUS.queued;
            const Icon = sc.icon;
            return (
              <div key={i} className={`card p-3 border ${sc.bg}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Icon size={13} className={sc.cls} />
                  <span className="text-sm font-medium text-slate-900">{step.id || step.name}</span>
                </div>
                <p className="text-xs text-slate-500 font-mono truncate">
                  {Array.isArray(step.args) ? step.args.join(' ') : step.name}
                </p>
              </div>
            );
          })}
        </div>

        {/* Logs */}
        <div className="lg:col-span-2 card flex flex-col">
          <div className="px-4 py-2.5 border-b border-slate-200 flex items-center justify-between">
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Build Logs</span>
            <div className="flex items-center gap-1.5">
              {wsStatus === 'live' && (
                <span className="flex items-center gap-1 text-xs text-purple-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-pulse" />
                  Live
                </span>
              )}
              {wsStatus === 'connecting' && (
                <span className="text-xs text-slate-500">Connecting…</span>
              )}
              {wsStatus === 'done' && (
                <span className="text-xs text-slate-500">Completed</span>
              )}
              {wsStatus === 'error' && (
                <span className="text-xs text-red-400">Connection error</span>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 font-mono text-xs space-y-0.5 max-h-[480px] bg-surface-0 rounded-b-xl">
            {logs.length === 0 && wsStatus === 'connecting' && (
              <div className="text-slate-500">Waiting for logs…</div>
            )}
            {logs.map((line, i) => (
              <div key={i} className="flex gap-3 hover:bg-slate-50 px-1 py-0.5 rounded">
                <span className={LOG_COLORS[line.level] || LOG_COLORS.log}>{line.text}</span>
              </div>
            ))}
            {isRunning && wsStatus === 'live' && (
              <div className="px-1 py-0.5">
                <span className="text-slate-400 animate-pulse">▋</span>
              </div>
            )}
            <div ref={logsEndRef} />
          </div>
        </div>
      </div>
    </div>
  );
}
